import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FOCUS_OPTIONS = [
  "Book Featured Talent",
  "Coaching — Speaker / Artist / Business Development",
  "Consulting & Business Development",
  "Creative Collaborations & Programming",
  "Podcast Inquiries",
  "General Contact",
];

const inputClass =
  "w-full bg-[#121212] border border-[#333333] text-white px-4 py-3 text-sm focus:border-[#e02020] focus:outline-none rounded-none placeholder:text-[#666666]";
const labelClass = "block text-xs font-mono uppercase tracking-[.14em] text-[#b5b5b5] mb-2";

export const ConnectForm = () => {
  const [searchParams] = useSearchParams();
  const focusParam = searchParams.get("focus");
  const talentParam = searchParams.get("talent");
  const initialFocus = FOCUS_OPTIONS.includes(focusParam) ? focusParam : FOCUS_OPTIONS[0];

  const [talentList, setTalentList] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    focus: initialFocus,
    talent: "",
    message: "",
    website: "", // honeypot
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/talent`)
      .then((res) => {
        const list = res.data || [];
        setTalentList(list);
        if (talentParam) {
          const match = list.find((t) => t.slug === talentParam || t.name === talentParam);
          if (match) {
            setForm((f) => ({ ...f, talent: match.name, focus: "Book Featured Talent" }));
          }
        }
      })
      .catch(() => {});
  }, [talentParam]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        focus: form.focus,
        talent: form.talent,
        message: form.message,
        honeypot: form.website,
      });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div data-testid="connect-success-message" className="border border-[#2e2e2e] bg-[#121212] p-10 text-center">
        <p className="font-disp text-3xl text-white mb-4">Thank you.</p>
        <p className="text-sm text-[#aaaaaa] leading-relaxed max-w-md mx-auto">
          Your inquiry has been received. We read every note and will be in touch shortly.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="connect-form" onSubmit={submit} className="space-y-6">
      {/* Honeypot — hidden from humans, traps bots */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={update("website")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="connect-name" className={labelClass}>
            Your Name
          </label>
          <input
            id="connect-name"
            data-testid="connect-name-input"
            type="text"
            required
            value={form.name}
            onChange={update("name")}
            placeholder="First and last name"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="connect-email" className={labelClass}>
            Email Address
          </label>
          <input
            id="connect-email"
            data-testid="connect-email-input"
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            placeholder="name@organization.com"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="connect-focus" className={labelClass}>
          Collaboration Focus
        </label>
        <div className="relative">
          <select
            id="connect-focus"
            data-testid="connect-focus-select"
            value={form.focus}
            onChange={update("focus")}
            className={`${inputClass} appearance-none pr-10`}
          >
            {FOCUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#b5b5b5] pointer-events-none" />
        </div>
      </div>

      <div>
        <label htmlFor="connect-talent" className={labelClass}>
          Collaborator (optional)
        </label>
        <div className="relative">
          <select
            id="connect-talent"
            data-testid="connect-talent-select"
            value={form.talent}
            onChange={update("talent")}
            className={`${inputClass} appearance-none pr-10`}
          >
            <option value="">No preference / Not sure yet</option>
            {talentList.map((t) => (
              <option key={t.slug} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#b5b5b5] pointer-events-none" />
        </div>
      </div>

      <div>
        <label htmlFor="connect-message" className={labelClass}>
          Project Brief
        </label>
        <textarea
          id="connect-message"
          data-testid="connect-message-textarea"
          rows={4}
          value={form.message}
          onChange={update("message")}
          placeholder="Tell us about your audience, timeline, or developmental goals..."
          className={inputClass}
        />
      </div>

      {error && (
        <p data-testid="connect-error-message" role="alert" className="text-sm text-[#e02020]">
          {error}
        </p>
      )}

      <button
        type="submit"
        data-testid="connect-submit-button"
        disabled={busy}
        className="w-full py-4 bg-[#e02020] text-white text-xs font-mono uppercase tracking-[.16em] hover:bg-white hover:text-[#121212] transition-colors font-semibold rounded-none disabled:opacity-60"
      >
        {busy ? "Sending…" : "Send Collaboration Inquiry →"}
      </button>
    </form>
  );
};
